import { Component, type ErrorInfo, type ReactNode } from 'react'
import { RefreshCw, AlertTriangle } from 'lucide-react'
import { isChunkLoadError, reloadForStaleChunk } from '@/lib/reload-on-stale-chunk'

interface Props {
  /** Optional label shown in the error UI so the user knows which surface failed. */
  scope?: string
  /** Optional fallback to override the default UI. */
  fallback?: ReactNode
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  isChunk: boolean
}

/**
 * Root-and-per-tree error boundary. Catches render errors so a single bad
 * component doesn't blank the entire app. Wrap each major route subtree
 * (customer, auth, admin) so a bug in one tree doesn't kill the others.
 *
 * Reporting: today this only logs to console. When Sentry / equivalent
 * comes online (audit item #3), call its capture from componentDidCatch.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, isChunk: false }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isChunk: isChunkLoadError(error) }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A failed lazy chunk usually means a deploy shipped new hashes — reload to
    // recover instead of showing the error UI.
    if (isChunkLoadError(error) && reloadForStaleChunk()) return
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.scope ?? 'root', error, info.componentStack)
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null })
    // Force a fresh server fetch — the in-memory React tree is stale.
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children
    // Stale chunk → a reload is in flight; show a neutral spinner, not the
    // "Something broke" panel.
    if (this.state.isChunk) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="h-10 w-10 rounded-full border-2 border-border border-t-accent animate-spin" />
        </div>
      )
    }
    if (this.props.fallback) return this.props.fallback

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-surface border border-border rounded-lg shadow-2 p-8 text-center space-y-4">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger-soft text-danger">
            <AlertTriangle className="h-7 w-7" />
          </span>
          <div className="space-y-1.5">
            <h2 className="text-xl font-bold tracking-tight text-text-strong">
              Something broke
            </h2>
            <p className="text-sm text-muted">
              {this.props.scope
                ? `An error in ${this.props.scope} stopped this page from rendering.`
                : 'An unexpected error stopped this page from rendering.'}{' '}
              You can reload — your work in other tabs is unaffected.
            </p>
            {this.state.error && (
              <p className="text-[11px] text-soft font-mono break-all mt-2">
                {this.state.error.message}
              </p>
            )}
          </div>
          <button
            onClick={this.handleReset}
            className="inline-flex items-center gap-2 rounded bg-accent text-accent-fg font-semibold text-sm h-10 px-4 shadow-brand hover:bg-accent-2 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Reload page
          </button>
        </div>
      </div>
    )
  }
}
